import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, PackageSearch } from 'lucide-react';
import ProductCard from './ProductCard';
import EmptyState from './EmptyState';
import { Product } from '../services/api.types';
import { fetchProducts } from '../services/product.api';
import { useTranslations } from '../translations';
import './ProductCarousel.css';

interface ProductCarouselProps {
  title: string;
  productType?: 'PRODUCT' | 'DEMAND';
  userId?: string | null;
  excludedIds?: string[];
  emptyTitle?: string;
  emptyMessage?: string;
  emptyActionLabel?: string;
  onNavigateToProductDetail: (productId: string) => void;
  isLoggedIn: boolean;
  onNavigateToLogin: () => void;
  onNavigateToSearch?: () => void;
  productsPerPage?: number;
  showViewAll?: boolean;
  className?: string;
}

const ProductCarousel: React.FC<ProductCarouselProps> = ({
  title,
  productType = 'PRODUCT',
  userId = null,
  excludedIds = [],
  emptyTitle,
  emptyMessage = 'Nu există anunțuri disponibile',
  emptyActionLabel,
  onNavigateToProductDetail,
  isLoggedIn,
  onNavigateToLogin,
  onNavigateToSearch,
  productsPerPage = 5,
  showViewAll = true,
  className = ''
}) => {
  const t = useTranslations();
  const [currentPage, setCurrentPage] = useState(0);
  const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [canGoNext, setCanGoNext] = useState(true);
  const isEmpty = !loading && displayedProducts.length === 0;

  // Use refs to avoid triggering re-renders
  const productCacheRef = useRef<Map<number, Product[]>>(new Map());
  const apiCallCountRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const listRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!loading) {
      listRef.current?.scrollTo({ left: 0, behavior: 'instant' });
    }
  }, [loading, displayedProducts]);

  const MAX_API_CALLS = 10;
  const PRODUCTS_PER_FETCH = 5;

  // Reset state when productType, userId, or excludedIds changes
  useEffect(() => {
    setCurrentPage(0);
    productCacheRef.current = new Map();
    apiCallCountRef.current = 0;
    hasMoreRef.current = true;
    setCanGoNext(true);
    setDisplayedProducts([]);
    setLoading(true);
  }, [productType, userId, JSON.stringify(excludedIds)]);

  // Load products for the current page
  useEffect(() => {
    const loadProductsForPage = async () => {
      // Prevent duplicate calls
      if (loadingRef.current) return;

      // Check if we already have this page cached
      if (productCacheRef.current.has(currentPage)) {
        setDisplayedProducts(productCacheRef.current.get(currentPage)!);
        setLoading(false);

        // Update canGoNext based on cache and hasMore
        const canNavigateNext = hasMoreRef.current || productCacheRef.current.has(currentPage + 1);
        setCanGoNext(canNavigateNext);
        return;
      }

      // Check if we've reached the API call limit
      if (apiCallCountRef.current >= MAX_API_CALLS) {
        // Redirect to search results
        if (onNavigateToSearch) {
          onNavigateToSearch();
        }
        return;
      }

      loadingRef.current = true;
      setLoading(true);

      try {
        const apiPage = currentPage + 1; // API uses 1-based pagination
        const data = await fetchProducts(
          apiPage,
          PRODUCTS_PER_FETCH,
          productType,
          null,
          [],
          userId,
          'PUBLISHED_AT',
          'DESC',
          excludedIds
        );

        const newProducts = data.content;

        // Update cache
        productCacheRef.current.set(currentPage, newProducts);

        setDisplayedProducts(newProducts);
        apiCallCountRef.current += 1;

        // Check if there are more products from the API
        const apiHasMore = apiPage < data.totalPages;
        hasMoreRef.current = apiHasMore;

        // Can go next if: we have more from API OR we have next page cached
        const canNavigateNext = apiHasMore || productCacheRef.current.has(currentPage + 1);
        setCanGoNext(canNavigateNext);
      } catch (error) {
        console.error('Error fetching products:', error);
        setDisplayedProducts([]);
        hasMoreRef.current = false;
        setCanGoNext(false);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadProductsForPage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, productType, userId, onNavigateToSearch, JSON.stringify(excludedIds)]);

  const handlePreviousPage = () => {
    if (loading || loadingRef.current) return;

    if (currentPage > 0) {
      setLoading(true);
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (loading || loadingRef.current || !canGoNext) return;

    const nextPage = currentPage + 1;

    // Check if next page is already cached
    if (productCacheRef.current.has(nextPage)) {
      setLoading(true);
      setCurrentPage(nextPage);
      return;
    }

    // Check if we've reached the API call limit
    if (apiCallCountRef.current >= MAX_API_CALLS) {
      // Redirect to search results
      if (onNavigateToSearch) {
        onNavigateToSearch();
      }
      return;
    }

    // Only navigate if we believe there are more products
    if (hasMoreRef.current) {
      setLoading(true);
      setCurrentPage(nextPage);
    }
  };

  return (
    <section
      className={`product-carousel-section ${
        showViewAll && onNavigateToSearch ? 'product-carousel-section-with-view-all' : ''
      } ${isEmpty ? 'product-carousel-section-empty' : ''} ${className}`}
    >
      <div className="product-carousel-header">
        <h2 className="product-carousel-title">{title}</h2>
        <div className="product-carousel-header-actions">
          {showViewAll && onNavigateToSearch && (
            <button type="button" className="product-carousel-view-all" onClick={onNavigateToSearch}>
              <span>{t.productCarousel.viewAll}</span>
              <ArrowRight size={18} aria-hidden="true" />
            </button>
          )}
          <div className="product-carousel-navigation">
            <button
              type="button"
              className="product-carousel-button"
              onClick={handlePreviousPage}
              disabled={loading || currentPage === 0}
              aria-label={t.productCarousel.previous}
              title={t.productCarousel.previous}
            >
              <ChevronLeft size={20} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="product-carousel-button"
              onClick={handleNextPage}
              disabled={loading || !canGoNext}
              aria-label={t.productCarousel.next}
              title={t.productCarousel.next}
            >
              <ChevronRight size={20} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className="product-carousel-container">
        <button
          type="button"
          className="product-carousel-button product-carousel-side-button"
          onClick={handlePreviousPage}
          disabled={loading || currentPage === 0}
          aria-label={t.productCarousel.previous}
          title={t.productCarousel.previous}
        >
          <ChevronLeft size={20} aria-hidden="true" />
        </button>

        <div ref={listRef} className="product-carousel-list" aria-busy={loading} aria-live="polite">
          {loading ? (
            <>
              <span className="product-carousel-status">{t.productCarousel.loading}</span>
              {Array.from({ length: productsPerPage }, (_, index) => (
                <div className="product-carousel-skeleton" aria-hidden="true" key={index}>
                  <div className="product-carousel-skeleton-image" />
                  <div className="product-carousel-skeleton-line short" />
                  <div className="product-carousel-skeleton-line" />
                  <div className="product-carousel-skeleton-line medium" />
                </div>
              ))}
            </>
          ) : displayedProducts.length > 0 ? (
            displayedProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                viewType="grid"
                onNavigateToProductDetail={onNavigateToProductDetail}
                isLoggedIn={isLoggedIn}
                onNavigateToLogin={onNavigateToLogin}
              />
            ))
          ) : (
            <EmptyState
              className="product-carousel-empty-state"
              icon={<PackageSearch />}
              title={emptyTitle ?? t.productCarousel.emptyTitle}
              description={emptyMessage}
              headingLevel={2}
            >
              {onNavigateToSearch && emptyActionLabel && (
                <button
                  type="button"
                  className="app-empty-state-primary"
                  onClick={onNavigateToSearch}
                >
                  {emptyActionLabel}
                  <ArrowRight size={18} aria-hidden="true" />
                </button>
              )}
            </EmptyState>
          )}
        </div>

        <button
          type="button"
          className="product-carousel-button product-carousel-side-button"
          onClick={handleNextPage}
          disabled={loading || !canGoNext}
          aria-label={t.productCarousel.next}
          title={t.productCarousel.next}
        >
          <ChevronRight size={20} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
};

export default ProductCarousel;
